import type { Control } from "react-hook-form";
import { useController } from "react-hook-form";

import Checkbox from "@/components/Checkbox";
import Chip, { ChipSet } from "@/components/Chip";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import Progress from "@/components/Progress";
import SubSectionHeading from "@/components/SubSectionHeading";

import {
  toggleCareerPlanOnboardingGoalId,
  type CareerPlanOnboardingFormValues,
  type CareerPlanOnboardingGoalGroup
} from "./careerPlanOnboarding";

export default function CareerPlanOnboardingGoalsStep({
  control,
  groups,
  loading,
  errorMessage,
  hasArchivedCareerGoals
}: {
  control: Control<CareerPlanOnboardingFormValues>;
  groups: CareerPlanOnboardingGoalGroup[];
  loading: boolean;
  errorMessage?: string;
  hasArchivedCareerGoals: boolean;
}) {
  const { field } = useController({ control, name: "selectedCareerGoalIds" });

  if (loading) {
    return (
      <div className="flex justify-center py-8" data-testid="career-plan-onboarding-goals-loading">
        <Progress />
      </div>
    );
  }

  if (errorMessage) {
    return <ErrorBoundary variant="inline" title="Unable to Load Career Goals" message={errorMessage} />;
  }

  const selectedIds = field.value;

  return (
    <div className="flex flex-col gap-6" data-testid="career-plan-onboarding-goals">
      <p className="text-headline-small">
        These are the Career Goals this person saved. Uncheck anything that is stale or off-limits for this plan.
      </p>
      {groups.length === 0 ? (
        <div data-testid="career-plan-onboarding-goals-empty">
          <EmptyState
            iconName="track_changes"
            title="No Career Goals"
            caption={
              hasArchivedCareerGoals
                ? "This person does not have active Career Goals."
                : "This person has not submitted Career Goals yet."
            }
          />
        </div>
      ) : (
        groups.map((group) => (
          <div className="flex flex-col gap-3" data-testid={`career-plan-onboarding-goals-${group.id}`} key={group.id}>
            <SubSectionHeading>{group.label}</SubSectionHeading>
            {group.presentation === "note" ? (
              group.items.map((item) => {
                const checked = selectedIds.includes(item.id);
                return (
                  <Checkbox
                    key={item.id}
                    name={`career-plan-onboarding-goal-${item.id}`}
                    checked={checked}
                    label={item.label}
                    align="start"
                    onChange={(nextChecked) => {
                      if (nextChecked === checked) return;
                      field.onChange(toggleCareerPlanOnboardingGoalId(selectedIds, item.id));
                    }}
                  />
                );
              })
            ) : (
              <ChipSet>
                {group.items.map((item) => {
                  const checked = selectedIds.includes(item.id);
                  return (
                    <Chip
                      key={item.id}
                      label={item.label}
                      selected={checked}
                      showCheckmark
                      onClick={() => {
                        field.onChange(toggleCareerPlanOnboardingGoalId(selectedIds, item.id));
                      }}
                    />
                  );
                })}
              </ChipSet>
            )}
          </div>
        ))
      )}
    </div>
  );
}
